export class SkillsMapper {
  constructor(skills = [], capabilities = [], people = []) {
    this.skills = skills;
    this.capabilities = capabilities;
    this.people = people;
    this.skillsById = this.createSkillsMap();
  }

  createSkillsMap() {
    const skillsMap = new Map();
    this.skills.forEach(skill => {
      skillsMap.set(skill.id, skill);
    });
    return skillsMap;
  }

  // Get skill details by ID
  getSkillById(skillId) {
    return this.skillsById.get(skillId);
  }

  // Get skill name by ID
  getSkillNameById(skillId) {
    const skill = this.skillsById.get(skillId);
    return skill ? skill.name : skillId;
  }

  // Validate that all skill IDs in capabilities exist in skills
  validateCapabilitySkills() {
    const issues = [];
    
    this.capabilities.forEach(capability => {
      if (capability.required_skills) {
        capability.required_skills.forEach(skillId => {
          if (!this.skillsById.has(skillId)) {
            issues.push({
              type: 'missing_skill',
              capability: capability.name,
              skillId: skillId,
              message: `Capability "${capability.name}" references missing skill: ${skillId}`
            });
          }
        });
      }
    });

    return issues;
  }

  // Validate that all skill IDs in people exist in skills
  validatePeopleSkills() {
    const issues = [];
    
    this.people.forEach(person => {
      if (person.skills) {
        person.skills.forEach(skillId => {
          if (!this.skillsById.has(skillId)) {
            issues.push({
              type: 'missing_skill',
              person: person.name,
              skillId: skillId,
              message: `Person "${person.name}" has missing skill: ${skillId}`
            });
          }
        });
      }
    });

    return issues;
  }

  // Get all validation issues
  validateAll() {
    return [
      ...this.validateCapabilitySkills(),
      ...this.validatePeopleSkills()
    ];
  }

  // Get skills that are not used by any capability or person
  getUnusedSkills() {
    const usedSkillIds = new Set();
    
    // Collect skills used in capabilities
    this.capabilities.forEach(capability => {
      if (capability.required_skills) {
        capability.required_skills.forEach(skillId => usedSkillIds.add(skillId));
      }
    });
    
    // Collect skills used by people
    this.people.forEach(person => {
      if (person.skills) {
        person.skills.forEach(skillId => usedSkillIds.add(skillId));
      }
    });
    
    // Find unused skills
    return this.skills.filter(skill => !usedSkillIds.has(skill.id));
  }

  // Get capabilities that require a specific skill
  getCapabilitiesForSkill(skillId) {
    return this.capabilities.filter(capability => 
      capability.required_skills && capability.required_skills.includes(skillId)
    );
  }

  // Get people who have a specific skill
  getPeopleForSkill(skillId) {
    return this.people.filter(person => 
      person.skills && person.skills.includes(skillId)
    );
  }

  // Get skills by category
  getSkillsByCategory(category) {
    return this.skills.filter(skill => skill.category === category);
  }

  // Get all categories
  getCategories() {
    const categories = new Set();
    this.skills.forEach(skill => {
      if (skill.category) {
        categories.add(skill.category);
      }
    });
    return Array.from(categories).sort();
  }

  // Get skills mapped to certifications
  getSkillsToCertificationsMap() {
    const map = new Map();
    this.skills.forEach(skill => {
      if (skill.related_certifications && skill.related_certifications.length > 0) {
        skill.related_certifications.forEach(cert => {
          if (!map.has(cert)) {
            map.set(cert, []);
          }
          map.get(cert).push(skill);
        });
      }
    });
    return map;
  }

  // Check if person has certification that covers a skill
  personHasCertificationForSkill(person, skillId) {
    const skill = this.getSkillById(skillId);
    if (!skill || !skill.related_certifications || !person.certifications) {
      return false;
    }
    
    return skill.related_certifications.some(cert => 
      person.certifications.includes(cert)
    );
  }

  // Get coverage analysis for a person against capabilities
  getPersonCapabilityCoverage(person, capability) {
    if (!capability.required_skills) {
      return { coverage: 1, details: [] };
    }

    const details = capability.required_skills.map(skillId => {
      const skill = this.getSkillById(skillId);
      const hasSkill = person.skills && person.skills.includes(skillId);
      const hasCertification = this.personHasCertificationForSkill(person, skillId);
      
      return {
        skillId,
        skillName: skill ? skill.name : skillId,
        hasSkill,
        hasCertification,
        covered: hasSkill || hasCertification
      };
    });

    const coveredCount = details.filter(d => d.covered).length;
    const coverage = coveredCount / capability.required_skills.length;

    return { coverage, details };
  }

  // Generate skill usage report
  generateSkillUsageReport() {
    const report = {
      totalSkills: this.skills.length,
      usedSkills: 0,
      unusedSkills: 0,
      skillsByCategory: {},
      topSkillsInCapabilities: [],
      topSkillsInPeople: []
    };

    // Count skill usage
    const skillUsageInCapabilities = new Map();
    const skillUsageInPeople = new Map();

    this.capabilities.forEach(capability => {
      if (capability.required_skills) {
        capability.required_skills.forEach(skillId => {
          skillUsageInCapabilities.set(skillId, (skillUsageInCapabilities.get(skillId) || 0) + 1);
        });
      }
    });

    this.people.forEach(person => {
      if (person.skills) {
        person.skills.forEach(skillId => {
          skillUsageInPeople.set(skillId, (skillUsageInPeople.get(skillId) || 0) + 1);
        });
      }
    });

    // Calculate used vs unused
    const usedSkillIds = new Set([...skillUsageInCapabilities.keys(), ...skillUsageInPeople.keys()]);
    report.usedSkills = usedSkillIds.size;
    report.unusedSkills = report.totalSkills - report.usedSkills;

    // Group by category
    this.skills.forEach(skill => {
      if (!report.skillsByCategory[skill.category]) {
        report.skillsByCategory[skill.category] = 0;
      }
      report.skillsByCategory[skill.category]++;
    });

    // Top skills in capabilities
    report.topSkillsInCapabilities = Array.from(skillUsageInCapabilities.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([skillId, count]) => ({
        skillId,
        skillName: this.getSkillNameById(skillId),
        count
      }));

    // Top skills in people
    report.topSkillsInPeople = Array.from(skillUsageInPeople.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([skillId, count]) => ({
        skillId,
        skillName: this.getSkillNameById(skillId),
        count
      }));

    return report;
  }
}